// Foliage and trunk palettes plus the colour helpers that sample and jitter
// them. Split out of scatterInstances.ts (which re-exports everything here).

import * as THREE from 'three';
import type { ScatterPropType } from './TerrainScatterMaterials.js';

// Module-level temp — mutated in jitterColor. Safe because JS is single-threaded.
const _hsl = { h: 0, s: 0, l: 0 };

// --- Palettes ---
// Muted, natural tones (the old single rgb(127,255,127) read as neon). Each
// list runs cool → warm / dark → light so the smooth field walks a gradient.

export const FOLIAGE_PALETTES: Partial<Record<ScatterPropType, readonly string[]>> = {
  pine: ['#25543a', '#2f6240', '#3a6a3c', '#467541'],
  spruce: ['#21493c', '#2a5542', '#346047', '#406a4c'],
  oak: ['#44662c', '#557a32', '#6a8a36', '#7f9440', '#98913f'],
  birch: ['#6f9440', '#83a247', '#9aad4d', '#b3a94a'],
  bush: ['#3d5f2c', '#4c6e30', '#5d7b35', '#6f7f3a'],
  grass: ['#6f8f3a', '#7f9a40', '#90a347', '#a7a24e'],
  // Wildflower mix — saturated hues (red/yellow/pink/purple/blue/white),
  // picked per instance so each patch reads as a mixed meadow.
  flower: ['#d1495b', '#f2c94c', '#ef8fb7', '#9b6dd0', '#7fb8e0', '#f6f1e0'],
  cactus: ['#4f7a4a', '#5d8752', '#6b9058'],
  // Jungle: saturated, deep emeralds (hot and wet), brighter ferns on the floor.
  palm: ['#2f8a3a', '#3a9a42', '#4aa84a', '#5db556'],
  jungleTree: ['#17562e', '#1d6633', '#257638', '#2f8640'],
  fern: ['#3f9a3f', '#4faa44', '#5fb64c', '#72be52'],
  rock: ['#6f6d68', '#7d7a73', '#8a867d', '#9a958a'],
};

export const TRUNK_PALETTES: Partial<Record<ScatterPropType, readonly string[]>> = {
  pine: ['#5a3e2b', '#654632', '#6f4e36'],
  spruce: ['#4a3426', '#553b2b', '#5f4331'],
  oak: ['#5e4431', '#6b4d37', '#77563d'],
  birch: ['#d8d4c8', '#e3dfd3', '#cfcabc'],
  palm: ['#8a6a43', '#7a5c3a', '#96744a'],
  jungleTree: ['#4a3626', '#553f2d', '#5f4734'],
  flower: ['#4a6b35', '#557a3c', '#5f8142'],
};

/** Sample a palette at t in [0,1), blending between adjacent stops. */
export function samplePalette(
  palette: readonly string[],
  t: number,
  out: THREE.Color,
): THREE.Color {
  const pos = Math.min(0.9999, Math.max(0, t)) * (palette.length - 1);
  const i = Math.floor(pos);
  const from = new THREE.Color(palette[i]);
  const to = new THREE.Color(palette[Math.min(i + 1, palette.length - 1)]);
  return out.copy(from).lerp(to, pos - i);
}

/** Tint jitter: small hue / lightness nudge so no two neighbours are identical. */
export function jitterColor(color: THREE.Color, j1: number, j2: number): THREE.Color {
  color.getHSL(_hsl);
  return color.setHSL(
    _hsl.h + (j1 * 2 - 1) * 0.015,
    _hsl.s * (0.92 + j2 * 0.12),
    Math.min(1, _hsl.l * (0.9 + j1 * 0.2)),
  );
}
